import React, { useCallback } from 'react';
import PropTypes from 'prop-types';

import Input from '../../../common2/Input';
import DateInput from '../../../common2/DateInput';
import ColumnsLayout from '../../../common/layout/ColumnsLayout';
import Column from '../../../common/layout/Column';
import WeightInput from './WeightInput';
import Uploader from '../../../common/Uploader/Uploader';
import InputHelpLink from '../InputHelpLink';
import ClaimStepClosableMessage from './ClaimStepClosableMessage';
import Locale from '../../../Translate/Locale';
import CarrierSelect from './CarrierSelect';
import Select from '../../../common2/Select';
import { recipientCountryOptions } from '../countries';

function ShippingInformation(props) {
  const {
    claim: {
      trackingCode,
      carrierCountryCode,
      recipientName,
      recipientAddress,
      recipientCity,
      recipientZipCode,
      recipientCountryCode,
      carrierCode,
      firstHubScanDate,
      ssHubScanDate,
      deliveryDate,
      warehouseDeliveryDate,
      weight,
      trackingScreenshot,
      type,
      postalReturnDeliveryDate,
    },
    carrierOptions = [],
    carrierCountryOptions = [],
    errorsWithTranslation = {},
    onChange,
    onBusy,
    noTrackingScreenshotRequiredMessage,
    isManual,
  } = props;
  const onInputChange = useCallback((value, name) => onChange({ [name]: value }), [onChange]);

  const showDeliveryInfo = type !== 'investigation' && (!!deliveryDate || !!postalReturnDeliveryDate);
  const showWarehouseDeliveryDate = !!warehouseDeliveryDate;
  const showFirstHubScan = !!firstHubScanDate || !!ssHubScanDate;
  const isCashOnDeliveryClaim = type === 'cash_on_delivery';

  const uploaderHelpLinkElement = (
    // eslint-disable-next-line max-len
    <InputHelpLink to="https://support.portal.sevensenders.com/support/solutions/articles/15000048062-submission-document-requirements#Tracking-Screen-Shot-Requirements%C2%A0">
      Tracking screenshot requirements
    </InputHelpLink>
  );

  return (
    <Column spacing="medium">
      <ColumnsLayout>
        <Input
          name="trackingCode"
          label="SHIPPING_INFO_TRACKING_ID"
          placeholder="Add tracking ID"
          isRequired
          value={trackingCode}
          error={errorsWithTranslation.trackingCode}
          disabled={!isManual}
          onChange={onInputChange}
        />
        <div />
        <CarrierSelect
          isDisabled
          carrierCode={carrierCode}
          carrierCountryCode={carrierCountryCode}
          carrierOptions={carrierOptions}
          carrierCountryOptions={carrierCountryOptions}
          errors={errorsWithTranslation}
          onChange={onChange}
        />
        {showFirstHubScan ? (
          <DateInput
            name="firstHubScanDate"
            label="First hub scan"
            placeholder="Add date"
            isRequired
            value={firstHubScanDate || ssHubScanDate}
            disabled
            onChange={onInputChange}
          />
        ) : (
          <div />
        )}
        {showWarehouseDeliveryDate ? (
          <DateInput
            name="warehouseDeliveryDate"
            label={'Warehouse delivery date'}
            value={warehouseDeliveryDate}
            disabled={true}
            isRequired={true}
          />
        ) : showDeliveryInfo ? (
          <DateInput
            name="deliveryDate"
            label={deliveryDate ? 'Delivery date' : 'Postal return delivery date'}
            placeholder="Add delivery date"
            value={deliveryDate || postalReturnDeliveryDate}
            onChange={onInputChange}
            isRequired
            disabled
          />
        ) : (
          <div />
        )}
      </ColumnsLayout>
      <ColumnsLayout>
        <Input
          name="recipientName"
          label="Recipient name"
          placeholder="Add name"
          isRequired
          value={recipientName}
          error={errorsWithTranslation.recipientName}
          onChange={onInputChange}
          noErrorTranslation
          disabled={!isManual}
        />
        <Input
          name="recipientAddress"
          label="Recipient address"
          placeholder="Add address"
          isRequired
          value={recipientAddress}
          error={errorsWithTranslation.recipientAddress}
          onChange={onInputChange}
          noErrorTranslation
          disabled={!isManual}
        />
        <Input
          name="recipientZipCode"
          label="Recipient zip code"
          placeholder="Add zip code"
          isRequired
          value={recipientZipCode}
          error={errorsWithTranslation.recipientZipCode}
          onChange={onInputChange}
          noErrorTranslation
          disabled={!isManual}
        />
        <Input
          name="recipientCity"
          label="Recipient city"
          placeholder="Add city"
          isRequired
          value={recipientCity}
          error={errorsWithTranslation.recipientCity}
          onChange={onInputChange}
          noErrorTranslation
          disabled={!isManual}
        />
        <Select
          name="recipientCountryCode"
          label="Recipient country"
          placeholder="Select country"
          options={recipientCountryOptions}
          value={recipientCountryCode}
          error={errorsWithTranslation.recipientCountryCode}
          onChange={onInputChange}
          noErrorTranslation
          isDisabled={!isManual}
          isRequired
          isSearchable
          menuPlacement="auto"
          useMenuPortal
        />
      </ColumnsLayout>
      {!isCashOnDeliveryClaim && (
        <ColumnsLayout>
          <WeightInput
            name="weight"
            label="Weight"
            value={weight}
            error={errorsWithTranslation.weight}
            onChange={onInputChange}
            noErrorTranslation
            isRequired
          />
        </ColumnsLayout>
      )}
      {noTrackingScreenshotRequiredMessage ? (
        <ClaimStepClosableMessage intent="success">
          <Locale>{noTrackingScreenshotRequiredMessage}</Locale>
        </ClaimStepClosableMessage>
      ) : (
        <Uploader
          name="trackingScreenshot"
          label="Tracking screenshot"
          topRightElement={uploaderHelpLinkElement}
          allowedFileTypes={['application/pdf', 'image/png', 'image/jpg', 'image/jpeg']}
          helpText="CLAIMS_SCREENSHOT_UPLOAD_HELP_TEXT"
          isRequired
          files={trackingScreenshot}
          errorWithTranslation={errorsWithTranslation.trackingScreenshot}
          onChange={onInputChange}
          onBusy={onBusy}
        />
      )}
    </Column>
  );
}

ShippingInformation.propTypes = {
  claim: PropTypes.shape({
    trackingCode: PropTypes.string,
    carrierCountryCode: PropTypes.string,
    recipientName: PropTypes.string,
    firstHubScanDate: PropTypes.object,
    ssHubScanDate: PropTypes.object,
    deliveryDate: PropTypes.object,
    postalReturnDeliveryDate: PropTypes.object,
    deliveryAttemptDate: PropTypes.object,
    trackingScreenshot: PropTypes.array,
    weight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    type: PropTypes.string,
  }).isRequired,
  errorsWithTranslation: PropTypes.object,
  onChange: PropTypes.func,
  onBusy: PropTypes.func,
  noTrackingScreenshotRequiredMessage: PropTypes.string,
  isManual: PropTypes.bool,
};

export default React.memo(ShippingInformation);
